import { GET_GROUPING } from '../../site-table/columns/grouping.store';

import { createElement } from 'src/app/core/helpers/dom';

import { createComponent } from '@angular/core';
import { TCustomDropdownParams } from 'src/app/project/components/dropdown/AbstractDropdown';
import { SearchBoxComponent } from 'src/app/project/components/search-box/search-box.component';
import { translate } from 'src/app/project/features/translate/translate';
import { TColumn } from '../../site-table/columns/column.model';
import { GET_COLUMNS } from '../../site-table/columns/columns.store';
import { GET_SORTING } from '../../site-table/columns/sorting.store';
import AbstractActionFieldsDropdown from '../actionDropdown/AbstractActionFieldsDropdown';
import { getFieldIconFromFile } from '../utils/get-field-icon';

export default class GroupByDropdown extends AbstractActionFieldsDropdown {
  private allColumns: TColumn[] = [];
  private keyword = '';

  constructor(_dropdownProperties: TCustomDropdownParams) {
    const modifiedColumns = _dropdownProperties.type === 'group' ? GET_GROUPING() : GET_SORTING();

    super(_dropdownProperties, modifiedColumns);

    if (!this.columns) {
      this.columns = GET_COLUMNS();
      modifiedColumns.forEach((groupOrSortColumn) => {
        this.columns = this.columns.filter((column) => column.name !== groupOrSortColumn.name);
      });
    }

    this.allColumns = this.columns;
  }

  // --------------------------------------------------
  // -------------------- ELEMENTS --------------------
  // --------------------------------------------------

  createContentElement(): HTMLElement {
    return createElement('div', {
      attrs: {
        class: 'groupByDropdown',
      },
      children: [
        this.createTitleElement(),
        this.createSearchElement(),
        this.createColumnSectionElement(),
      ],
    });
  }

  createTitleElement(): HTMLElement {
    return createElement('div', {
      attrs: {
        class: 'groupByDropdown__title',
      },
      children: [translate('select_a_field')],
    });
  }

  createSearchElement(): HTMLElement {
    const host = createElement<HTMLElement>('div', {
      attrs: {
        class: 'groupByDropdown__searchHost',
      },
    });

    if (!this.environmentInjector || !this.applicationRef) {
      return host;
    }

    const ref = createComponent(SearchBoxComponent, {
      environmentInjector: this.environmentInjector,
    });

    ref.setInput('ppPlaceholder', translate('filter_items'));
    ref.setInput('ppFocusOnInit', true);
    ref.setInput('ppSize', 'wide');

    ref.instance.ppAction.subscribe((keyword: string) => {
      this.keyword = keyword;
      this.refreshColumnSection();
    });

    this.applicationRef.attachView(ref.hostView);
    ref.changeDetectorRef.detectChanges();
    host.appendChild(ref.location.nativeElement);
    this.componentRefs.push(ref);

    return host;
  }

  createColumnSectionElement(): HTMLElement {
    this.columnSectionElement = createElement('div', {
      children: this.buildColumnChildren(),
    });

    return this.columnSectionElement;
  }

  createFieldElement(column: TColumn): HTMLElement {
    const fieldElement = createElement('div', {
      attrs: {
        class: 'dropdownItem dropdownItem__group',
      },
      eventListeners: {
        click: () => {
          this.onSelect(column);
          this.update();
          this.destroy();
        },
      },
      children: [createFieldIcon(column), createNameElement(column.name)],
    });

    return fieldElement;
  }

  private refreshColumnSection(): void {
    if (!this.columnSectionElement) {
      return;
    }

    while (this.columnSectionElement.firstChild) {
      this.columnSectionElement.removeChild(this.columnSectionElement.firstChild);
    }

    this.buildColumnChildren().forEach((child) => {
      const node = typeof child === 'string' ? document.createTextNode(child) : child;
      this.columnSectionElement.appendChild(node);
    });
  }

  private buildColumnChildren(): (HTMLElement | string)[] {
    const keyword = (this.keyword ?? '').trim().toLowerCase();
    const source = this.allColumns ?? this.columns ?? [];
    this.columns = keyword
      ? source.filter((column) => column.name.toLowerCase().includes(keyword))
      : source;

    if (this.columns.length) {
      return this.columns.map((column) => this.createFieldElement(column));
    }

    return [this.createNoResultsElement()];
  }

  private createNoResultsElement(): HTMLElement {
    return createElement('div', {
      attrs: {
        class: 'groupByDropdown__noResults',
      },
      children: [translate('no_more_fields_to_choose_from')],
    });
  }
}

function createFieldIcon(column: TColumn): HTMLElement {
  const iconElementWrapper = createElement('div', {
    attrs: {
      class: 'iconElementWrapper',
    },
  });

  iconElementWrapper.innerHTML = getFieldIconFromFile(column);

  return iconElementWrapper;
}

function createNameElement(name: string): HTMLElement {
  const element = createElement('span', {
    attrs: {
      class: 'dropdownItem__groupName',
      title: name,
    },
    children: [name],
  });

  return element;
}
